import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText, Loader2, Upload } from 'lucide-react';
import { PhaseNav, type PhaseNavItem } from '../PhaseNav';
import { readPlace, writePlace } from '../lastPlace';
import { useScrollMemory } from '../scrollMemory';
import { FIRST_TAB, MODELS_TAB, PIPELINE, currentTab, phaseNumberOf, phaseTabAt } from '../viewerPipelines';
import { UploadPanel } from './UploadPanel';
import { useParkedSubmodelFilter } from '../SubmodelPicker';
import { RunControls } from '../RunControls';
import { DocSidebar } from '../DocSidebar';
import {
  RUN_ALL_LAST_PHASE, RUN_ALL_PHASE_COUNT, RUN_ALL_PHASES, resumeAllSteps, resumePoint, runAllSteps,
  type RunAllOutcome,
} from '../runAll';
import { haltRuns } from '../runHalt';
import { useRun } from '../categoryRun';
import { usePartsRun } from '../partsRun';
import { useStepParts } from '../stepPartsRun';
import { useStepsRun } from '../stepsRun';
import { useImagesBusy } from '../imagesRefresh';
import { EtapeSection } from '../EtapeSection';
import { SECTION_WIDTH } from '../sectionWidth';
import { FolderDoc, getFile, isFolder, resolveDoc, type NoticeFile } from '../noticeStore';
import { docReady, hydrateDoc } from '../docState';
import { useSaveListingPhotos } from '../listingPhotosSave';
import { ModelSizeSync } from '../ModelSizeSync';
import { SignOffSync } from '../SignOffSync';
import { AppScreenMemory } from '../AppScreenMemory';
import { UnsavedBanner } from '../UnsavedBanner';
import { ensurePartImages } from '../realImages';
import { useNoticeFiles } from '../useNoticeFiles';
import { chainRun, endChainRun, setChainPaused, startChainRun } from '../chainRun';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SandboxSaveWalk } from '../SandboxSaveWalk';
import { IS_SANDBOX } from '../../../apiBase';
import { PhaseNumber } from '../phaseBusy';

// The "Reader" sidebar tab links to /notice/view without an id, so returning to
// it would otherwise lose the open document. We remember the last opened id here
// and reopen it.
//
// localStorage, and staying there while everything else moved to the server: which
// manual THIS browser had open is not a fact about the manual, and pushing it would
// mean two admins dragging each other's readers around.
const LAST_DOC_KEY = 'notice.lastDocId';

// Elapsed seconds -> "m:ss" for the Run-all chronometer.
const fmtElapsed = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// THE RUN'S COUNTER IS IN STAGES, one higher than the pipeline counts them because the manual
// itself is the first: "3/9" is the third thing done of nine, several of which can belong to one
// phase. Derived from the pipeline (RUN_ALL_PHASE_COUNT), so it is always what the button runs.
const PHASE_OFFSET = 1;
const CHAIN_LAST_STAGE = PHASE_OFFSET + RUN_ALL_PHASE_COUNT;
// AND THE PHASES IT IS ON ITS WAY TO ARE PHASES, which is a different number: the stage count
// stood in for it, and marked phases the run never reaches as coming the day a phase took two
// stages. See RUN_ALL_LAST_PHASE.
const CHAIN_LAST_PHASE = RUN_ALL_LAST_PHASE;

// The Viewer page: upload a PDF (button or drag-and-drop) and read it page by
// page. When the route carries an :id, that stored document is shown.
export function NoticeViewer({ fresh = false }: {
  // /view/new: start a manual, whatever was last open. Without it a viewer with no
  // id reopens the last document, which is what /view used to be for — that address
  // is the list of manuals now, and this is the one page that means "a new one".
  fresh?: boolean;
}) {
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { add } = useNoticeFiles();
  const { t } = useLanguage();

  const [current, setCurrent] = useState<NoticeFile | null>(null);
  // The open document's id, for callbacks that need it without being rebuilt every time
  // a different manual is opened.
  const currentDocId = useRef('');
  useEffect(() => {
    currentDocId.current = current?.id ?? '';
  }, [current?.id]);
  // Which phase is open. Only that section renders; the bar is the sole way to switch.
  const [activeTab, setActiveTab] = useState(FIRST_TAB);
  // The phase cards, in phase order: PhaseNav pairs the nth item with the nth phase of
  // usePhaseProgress. The manual is in Phase 1's Manual subphase, not a card of its own.
  // A FOLDER, a document with no manual (see createFolder), has the models phase alone: every
  // other phase reads the PDF it does not have.
  const folder = isFolder(current);
  const phases = useMemo(() => (folder ? PIPELINE.filter((p) => p.id === MODELS_TAB) : PIPELINE), [folder]);
  // The submodel every phase reads, kept on a real one whichever phase is open.
  useParkedSubmodelFilter(current?.id ?? '');
  // ?phase=<id>: arrive on a phase rather than on the manual. What the Parts DB links back
  // with, so a part looked up in the database opens where it was read rather than on page
  // one of the PDF with a dozen tabs to guess between.
  //
  // Only if the id is one this pipeline has: a link naming a phase the other pipeline owns
  // would otherwise select a tab that renders nothing.
  // A NUMBER, THE WAY THE BADGES ARE NUMBERED — "?phase=2" is the parts. Not the
  // section's id: those are the historical `notice-step-N` strings and they stopped matching
  // the order long ago, so an address built from them said "step 6" over a screen showing
  // Phase 7. See phaseTabAt, which is the one place the two are related.
  //
  // AN ID IS STILL ACCEPTED, because links carrying one exist — the Parts DB has written them
  // for as long as this parameter has been read, and somebody's bookmark has one in it. A
  // value that is all digits is a position; anything else is tried as an id.
  const wantedPhase = (() => {
    const raw = (search.get('phase') || '').trim();
    if (!raw) return '';
    return /^\d+$/.test(raw) ? (phaseTabAt(Number(raw)) ?? '') : raw;
  })();
  // A phase id this pipeline actually has, or null. The one guard both the link and the
  // remembered phase need — a tab that matches no section renders an empty page under a
  // nav bar with nothing selected.
  //
  // A tab that is not a phase any more is read as the phase it went into (see currentTab): a
  // remembered position or a bookmark from before lands on the phase, not nowhere.
  const knownPhase = useCallback(
    (tab: string | null) => {
      const id = tab && currentTab(tab);
      return id && phases.some((p) => p.id === id) ? id : null;
    },
    [phases],
  );

  const navItems = useMemo<PhaseNavItem[]>(
    () => phases.map((p) => ({ id: p.id, label: t(p.labelKey) })),
    [phases, t],
  );

  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(false);
  // The document opened but its answers did not arrive — the reader is showing defaults
  // and saving nothing. Kept as state rather than read from docState on render, because
  // it changes exactly twice (when the hydrate resolves, and when a retry succeeds).
  const [unsaved, setUnsaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [chainDone, setChainDone] = useState(false);
  // A run that stopped short and can be picked up: the user paused it, or Phase 1
  // found a disagreement only they can settle. The session stays open while it holds
  // — nothing is running, but the run is not over either, so the phases keep showing
  // the run's state rather than their own idle one.
  //
  // Held is not running, and the difference shows: the clock and the progress count
  // go (they describe calls in flight), "Run all" comes BACK (it is how you abandon a
  // suspended run and start over), and a phase the run never reached says it is
  // paused rather than spinning at a step that is not coming.
  const [pausedRun, setPausedRun] = useState(false);
  // A pause asked for but not yet in effect. Pausing does not abandon the phase on
  // screen, so it only takes hold when that phase finishes — which can be minutes.
  // Without this the button would look ignored for all of them.
  const [pauseRequested, setPauseRequested] = useState(false);
  // A suspended run restored from a PREVIOUS session rather than lived through in
  // this one. Identical to `pausedRun` in every way but the clock: this run's
  // elapsed time was measured in a tab that is gone, and a chronometer reading 0:00
  // beside a run that took two minutes yesterday is a worse answer than no
  // chronometer. Cleared the moment the run is resumed — from then on the clock
  // times the resumed part, which is a real reading again.
  const [restoredRun, setRestoredRun] = useState(false);
  // A SANDBOX SAVE ASKED FOR: the Saves tab sends the reader here with ?capture=1, because
  // the phases it photographs only exist on this page. Read once into state rather than
  // off the address on every render — the walk drives the phase bar, and a walk restarted
  // by its own re-render is a second save of everything.
  //
  // IS_SANDBOX is the guard and not the flag's absence: the parameter is a URL anybody can
  // type, and on prod there is no route to save it to.
  const [walking, setWalking] = useState(() => IS_SANDBOX && search.get('capture') === '1');
  const inputRef = useRef<HTMLInputElement>(null);

  // HOW TALL THE PINNED HEADER IS, published to everything under it as a CSS variable.
  //
  // A phase that wants a bar of its own pinned — Phase 2's part selector — has to sit below
  // this one, and `top: 0` would put it underneath it instead. The offset it needs is the
  // header's height, and that is not a number anybody can write down: the phase cards wrap
  // onto two rows at a narrow width and back onto one when the sidebar collapses, and the
  // count of them is the pipeline's business (see viewerPipelines).
  //
  // So it is MEASURED and handed down rather than agreed on. The header stays the only thing
  // that knows its own size, a phase says `top: var(--notice-header)` without knowing what is
  // above it, and neither has a constant to keep in step with the other.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    // TO THE WHOLE PIXEL, and only when it moves. A bounding rect is a float, and the
    // header's height genuinely depends on the width — the phase cards wrap onto two rows
    // when it is narrow — while the width depends on whether the window has a scrollbar,
    // which depends on how tall the page is. A reader long enough to sit on that boundary
    // makes the three chase each other, and a fresh float every time is a re-render every time.
    const measure = () => setHeaderH((was) => {
      const now = Math.round(el.getBoundingClientRect().height);
      return now === was ? was : now;
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // The header only exists once a document is open, so the ref is only there to observe
    // from that render on.
  }, [current?.id]);

  // Live busy state of each runnable phase. One at a time now — the chain waits for
  // the image search before starting the step analysis — so the LOWEST-numbered busy
  // step is simply the one that is running. It stays a min() because that is still the
  // right answer if two ever overlap again, and reading it as "the current phase" is
  // what everything below does.
  const docId = current?.id ?? '';
  const classifyBusy = !!useRun(docId).busy;
  // The listing's photographs of a known side, saved on the server as they are placed.
  useSaveListingPhotos(docId);
  // Two calls, one lamp: the parts list (Phase 2) and the repartition among the steps
  // (Phase 3, its own phase in the reader since it got its own run button). They are
  // consecutive stages of the CHAIN, and the lamps below are numbered by the chain rather
  // than by the reader, so the pair lights one.
  //
  // Two statements, and they have to stay two: `a().busy || b().busy` would stop calling
  // the second hook the moment the first one reported busy, which is a hook that runs on
  // some renders and not others. React counts hooks by position, so the whole reader
  // crashed the instant this phase started working.
  const partsListBusy = usePartsRun(docId).busy;
  const allocBusy = useStepParts(docId).busy;
  const partsBusy = partsListBusy || allocBusy;
  const imagesBusy = useImagesBusy(docId);
  const stepsBusy = useStepsRun(docId).busy;
  // Numbered by position in the CHAIN, which is what the "· N/4" counter is counting.
  // The real images are missing on purpose: that phase is run by hand, part by part, so
  // it occupies no stage — giving it one made the count skip, and once the step analysis
  // joined the chain behind it, overshoot ("5/4").
  const busySteps = [
    classifyBusy ? 1 : 0,
    partsBusy ? 2 : 0,
    stepsBusy ? 3 : 0,
  ].filter((n) => n > 0);
  const currentStep = busySteps.length ? Math.min(...busySteps) : 0;
  // Any pipeline phase still working. The run-all session is not over while ONE of them
  // is, whatever the awaited chain thinks: a phase runner no-ops when it is already
  // busy (`if (busy) return`), so the chain's own await can resolve while that phase
  // keeps going — and the session must not end under a phase that is still lit.
  const anyPhaseBusy = classifyBusy || partsBusy || imagesBusy || stepsBusy;
  // Each step panel derives its own "Waiting on previous steps" state from `locked`
  // (the run-all session flag) plus its own busy/done state, so the parent only has
  // to tell it a session is in flight — and, via `held`, whether that session is
  // moving, which is the difference between waiting and waiting forever.
  // `currentStep` below drives the run-all button's "· N/4" progress label and the
  // "jump to current step" control.
  const lastStepRef = useRef(0);
  useEffect(() => {
    if (currentStep > 0) lastStepRef.current = currentStep;
  }, [currentStep]);
  const shownStep = currentStep || lastStepRef.current;

  // Reopen a run an earlier session left suspended. Its results came back from
  // localStorage with the stores, and now the point it stopped at comes back too —
  // but nothing is in flight and no state here knows there was ever a run, so
  // without this the screen offers only "Run all", which redoes what it is showing.
  // Runs on mount and whenever a different notice is opened.
  useEffect(() => {
    // A chain still in flight in THIS tab is the stronger fact, and it is asked first.
    // Leaving the reader for another tab unmounts it, and coming back used to arrive at
    // "no run" — so every phase the chain had not reached yet dropped its "coming"
    // hatching and drew an empty bar, beside phases that were visibly still working.
    const live = docId ? chainRun(docId) : null;
    const suspended = !!docId && resumePoint(docId) !== null;
    setRunningAll(!!live || suspended);
    setPausedRun(live ? live.paused : suspended);
    // Only a run restored from a previous SESSION suppresses the clock. One this tab is
    // still driving has a real elapsed time; it simply lost its component.
    setRestoredRun(!live && suspended);
    setChainDone(false);
    setPauseRequested(false);
    lastStepRef.current = 0;
  }, [docId]);

  // End the "Run all" session once the awaited chain has returned AND no phase is still
  // working. Both halves are needed: "chain returned" alone can be true while a phase
  // the chain no-op'd (its `if (busy) return` guard) keeps running, and "all idle"
  // alone misfires in the brief gaps between phases. Together they mean the run is over.
  // A paused run is not a finished one: it keeps the session open until it is either
  // resumed to the end or stopped.
  useEffect(() => {
    if (runningAll && chainDone && !anyPhaseBusy && !pausedRun) {
      if (current) endChainRun(current.id);
      setRunningAll(false);
      setChainDone(false);
      lastStepRef.current = 0;
    }
  }, [runningAll, chainDone, anyPhaseBusy, pausedRun]);

  // Elapsed-time chronometer for the "Run all" session: ticks each second while the
  // pipeline works, freezes on its reading while paused (time spent waiting on the
  // user is not time the run took), and resets when the session ends.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!runningAll) {
      setElapsed(0);
      return;
    }
    if (pausedRun) return;
    // Count from where the clock froze, so resuming continues rather than restarts.
    const start = Date.now() - elapsed * 1000;
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
    // `elapsed` is read to seed the resumed clock, not tracked: listing it here
    // would tear the interval down and rebuild it every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningAll, pausedRun]);

  // Drive the chain and react to how it ended. Both 'paused' and 'needsPick' leave
  // the run suspended rather than over — the phases already done keep their results
  // and Resume picks up at the next one.
  const drive = (start: () => Promise<RunAllOutcome>) => {
    if (!current || numPages < 1) return;
    startChainRun(current.id, CHAIN_LAST_PHASE);
    setRunningAll(true);
    setChainDone(false);
    setPausedRun(false);
    setPauseRequested(false);
    setRestoredRun(false);
    void start()
      .then((outcome) => {
        const held = outcome === 'paused' || outcome === 'needsPick';
        // Recorded from the promise and not from the effect below, because the effect
        // needs a mounted reader and this does not. Leave for the Parts DB tab while a
        // chain is running, let it finish there, and the effect never fires — the record
        // would still say "running" on return, which is the same bug this file just fixed
        // wearing the opposite sign.
        if (held) setChainPaused(current.id, true);
        else endChainRun(current.id);
        setPausedRun(held);
        setPauseRequested(false);
        // Phase 1's own banner states the question; landing on it is all this has
        // to do.
        if (outcome === 'needsPick') setActiveTab('notice-step-1');
      })
      .catch(() => endChainRun(current.id))
      .finally(() => setChainDone(true));
  };

  // Run every reader step for the open document (classification -> parts -> per-step
  // analysis; the real-image search follows once parts exist).
  // Also the way OUT of a suspended run: `from` is null, so every phase is cleared
  // and Phase 1 opens a new run. The guard has to match the button's own disabled
  // rule — a suspended run has `runningAll` set, and refusing it here would leave the
  // button live and inert.
  const onRunAll = () => {
    if (!current || (runningAll && !pausedRun) || numPages < 1) return;
    drive(() => runAllSteps(current.id, current.data, numPages, t));
  };

  // Stop launching calls. Nothing in flight is abandoned — it is billed either way,
  // so it lands and is kept — and the rest of the chain waits behind Resume, which
  // re-asks only for the items this phase never reached.
  const onPauseAll = () => {
    if (!current) return;
    setPauseRequested(true);
    haltRuns(current.id);
  };

  // Pick the chain up at the phase it never got to.
  const onResumeAll = () => {
    if (!current) return;
    drive(() => resumeAllSteps(current.id, current.data, numPages, t));
  };

  // WHERE A MANUAL OPENS, whenever a different one is opened — including the one a refresh
  // reopens, which is the case this exists for. Three answers, in order:
  //
  //   the ?phase= link   what the arriving address asked for. The Parts DB links back with
  //                      it, and a saved position must not overrule a link somebody
  //                      followed on purpose.
  //   the remembered one this browser's own last position in THIS manual, in THIS
  //                      pipeline. A phase remembered from another manual does not match
  //                      and does not apply — a different notice still opens at the top.
  //   the document       what every manual opened at before any of this.
  //
  useEffect(() => {
    if (!current?.id) {
      setActiveTab(FIRST_TAB);
      return;
    }
    setActiveTab(knownPhase(wantedPhase)
      ?? knownPhase(readPlace(current.id, 'phase'))
      ?? phases[0].id);
    // On the DOCUMENT changing and nothing else. The link and the memory are read as they
    // stand at that moment; re-running when either changed would drag the viewer off a
    // phase somebody had since chosen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  // HOW FAR DOWN, per manual and per phase. The phase is remembered above; this is the
  // other half of coming back to where you were, and the phases are long enough that the
  // two are different questions.
  useScrollMemory(current?.id ? `${current.id}:${activeTab}` : null);

  // And remembering it. Every way of changing the phase goes through `activeTab` — the
  // nav bar, the ?phase= link, Phase 1 sending you to the parts table — so this is the one
  // place that has to write, rather than each of them.
  useEffect(() => {
    if (current?.id) writePlace(current.id, 'phase', activeTab);
  }, [current?.id, activeTab]);

  // AND SAYING SO IN THE ADDRESS BAR. `?phase=` was read and never written: the Parts DB
  // linked back with it, and the reader's own position was kept in localStorage alone — so
  // the address said "this manual" while the screen said "this manual, Phase 7", and a link
  // sent to somebody landed them wherever THEY were last.
  //
  // REPLACE AND NOT PUSH. The address becomes shareable, bookmarkable and survives a refresh
  // either way; pushing would also make Back walk the phases somebody had clicked through,
  // and after eight of those Back stops being the way out of the notice. What the URL is for
  // here is naming where you are, not recording how you got there.
  //
  // EVERY PHASE, THE MANUAL INCLUDED. It used to leave the top unwritten, on the reasoning
  // that a bare address is what somebody pastes when they mean "this manual" — but a bar that
  // numbers the upload 1 and an address that calls it nothing are two answers to one
  // question, and the whole point here is that the address says where you are.
  //
  // localStorage keeps its job unchanged: it is what answers when the address names no phase
  // at all, which is every arrival at a URL nobody built from this app. Both are read, in
  // that order, by the effect above.
  //
  // ON THE TAB CHANGING, AND ON NOTHING ELSE — which is what keeps this from erasing the
  // very link it is meant to honour.
  //
  // The effect above resolves where a manual opens by calling setActiveTab, so on the pass
  // where a document first lands `activeTab` is still the PREVIOUS value: waking on
  // `current.id` too would see the previous tab, strike ?phase= out of the address, and put it
  // back a render later. Woken by the tab alone, this first runs with the value that effect
  // chose. `search` is deliberately absent for the same kind of reason — it is written here,
  // and depending on it is an effect that re-runs on its own writes.
  useEffect(() => {
    if (!current?.id) return;
    const n = phaseNumberOf(activeTab);
    // A tab this pipeline does not have has no number to write, and writing 0 would be an
    // address naming a phase that does not exist.
    if (!n) return;
    if ((search.get('phase') || '') === String(n)) return;
    const next = new URLSearchParams(search);
    next.set('phase', String(n));
    setSearch(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load the selected document from IndexedDB when the id changes. With no id and
  // not a deliberately fresh page, reopen the last document we still have.
  useEffect(() => {
    let cancelled = false;

    if (!id) {
      const last = fresh ? null : localStorage.getItem(LAST_DOC_KEY);
      if (!last) {
        setCurrent(null);
        return;
      }
      setLoading(true);
      getFile(last).then((f) => {
        if (cancelled) return;
        if (f) {
          navigate(`/view/${f.slug || last}`, { replace: true });
        } else {
          localStorage.removeItem(LAST_DOC_KEY);
          setCurrent(null);
          setLoading(false);
        }
      });
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    // The URL token is a friendly slug or a raw SHA; resolve it to the real doc id
    // first (falling back to treating it AS an id, so an unresolved SHA still tries).
    // Then the answers BEFORE the document: the stores read their state synchronously
    // in their initializers, so a read that lands before the state does sees a
    // document nobody has run and can save that back over a run somebody did. Bytes
    // and answers are awaited together — they are one document.
    void resolveDoc(id).then((res) => {
      if (cancelled) return;
      const docId = res?.id ?? id;
      // Phase 2's images are ASKED FOR NOW, beside the rest, but NOT WAITED FOR: the list was
      // the whole picture of every render, 8.8 MB on one manual, and the manual stayed a
      // spinner for the four seconds it took. It is ids and hashes now (see get_part_images)
      // and lands a moment after; its epoch bump fills Phase 2's bar when it does.
      void ensurePartImages(docId);
      void Promise.all([getFile(docId), hydrateDoc(docId)]).then(([f]) => {
        if (cancelled) return;
        setCurrent(f ?? null);
        setLoading(false);
        setUnsaved(!!f && !docReady(docId));
        if (f) {
          localStorage.setItem(LAST_DOC_KEY, f.id);
          // Show the friendly slug in the address bar when we arrived by SHA.
          if (f.slug && id !== f.slug) navigate(`/view/${f.slug}`, { replace: true });
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [id, fresh, navigate]);

  const onPick = useCallback(
    async (file: File | undefined) => {
      setRejected(false);
      if (!file) return;
      if (file.type !== 'application/pdf') {
        setRejected(true);
        return;
      }
      setBusy(true);
      try {
        const rec = await add(file);
        navigate(`/view/${rec.id}`);
      } finally {
        setBusy(false);
      }
    },
    [add, navigate],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      void onPick(e.dataTransfer.files?.[0]);
    },
    [onPick],
  );

  // Shared handlers for both drop zones (the open-document viewer and the
  // empty-state square).
  const dropZoneProps = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop,
  };

  // THE MANUAL, as Phase 1's Manual subphase shows it (see PhaseCtx.manual). Built here
  // because everything it needs is held here: the file, the page count, the drag state.
  const manual = current && !folder ? (
    <UploadPanel
      docId={current.id}
      file={current.data}
      onNumPages={setNumPages}
      dropZoneProps={dropZoneProps}
      dragging={dragging}
      t={t}
    />
  ) : null;

  // pb is generous so Phase 2's floating "sent to the generator" tooltip, which
  // overhangs the content, still lands on the page background rather than spilling
  // past its bottom edge.
  return (
    <div className="flex min-h-[24rem] flex-col px-4 pb-48 pt-4 sm:px-6">
      {/* Hidden picker, shared by the viewer's upload button and the drop zone */}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          void onPick(e.target.files?.[0]);
          e.target.value = ''; // allow re-picking the same file
        }}
      />

      {current ? (
        // Open document: a tabbed reader. The sticky bar switches which single
        // section shows; every step stays mounted (hidden) so "Run all" keeps
        // driving them and their in-flight work survives a tab switch.
        <div
          className="mx-auto flex w-full max-w-6xl flex-col"
          style={{ '--notice-header': `${headerH}px` } as CSSProperties}
        >
          {/* Above the phase cards, and above the sticky header: it is about the whole
              document, and it must not scroll away from somebody about to launch a run
              that would not be kept. */}
          {unsaved && (
            <UnsavedBanner docId={current.id} onRecovered={() => setUnsaved(false)} />
          )}
          {/* WHAT EVERY PART'S 3D MODEL MEASURES, kept in step with its Parts DB model whatever is
              open — see ModelSizeSync. Draws nothing. */}
          <ModelSizeSync docId={current.id} />
          {/* AND EVERY SIGN-OFF GIVEN ON A MODEL THAT HAS SINCE CHANGED IS WITHDRAWN — see
              SignOffSync. Draws nothing. */}
          <SignOffSync docId={current.id} />
          {/* AND WHAT THE OPEN PHASE ASKS OF THE DEVICE, for Phase 7's table. Draws nothing. */}
          <AppScreenMemory docId={current.id} phaseId={activeTab} />
          {/* The reader's header: the phase cards. A pipeline toggle used to sit above
              them, back when there were two; the height is measured (--notice-header,
              above), which is what lets a phase pin a bar under it without either of them
              holding a number about the other.

              NOT sticky, and with no background of its own: it stays where it sits in the
              page and scrolls away with everything else. Pinned, it needed an opaque fill
              to stop content showing through — and that fill was `bg-slate-100/95`, which
              was invisible only because the content pane behind it was `bg-slate-100` too.
              With the pane transparent over the blueprint, the same fill reads as a pale
              band across the page, and the transparent version it would otherwise need
              looks glitchy as content passes under it. Each pill carries its own white
              card now (see PhaseNav), so the bar has nothing left to draw. */}
          <div
            ref={headerRef}
            className="mb-6"
          >
            <PhaseNav
              items={navItems}
              active={activeTab}
              onSelect={setActiveTab}
              docId={current.id}
              docLoaded={numPages >= 1}
              // What the run is still going to do. A live, moving session will reach every
              // phase of the chain, so those are the ones marked as coming; a paused one is
              // not on its way anywhere until Reprendre, and passes null.
              incomingPhases={runningAll && !pausedRun ? RUN_ALL_PHASES : null}
            />
            {/* NEW RUN, THE RUN AND THE SUBMODEL, for the whole document: drawn in the sidebar
                (a portal), on every phase. */}
            <DocSidebar
              docId={current.id}
              disabled={runningAll && !pausedRun}
              submodelIdle={!phases.find((p) => p.id === activeTab)?.submodel}
              runControls={
                <RunControls
                  numPages={numPages}
                  runningAll={runningAll}
                  pausedRun={pausedRun}
                  pauseRequested={pauseRequested}
                  progressText={
                    runningAll && !pausedRun && shownStep > 0
                      ? ` · ${shownStep + PHASE_OFFSET}/${CHAIN_LAST_STAGE}`
                      : ''
                  }
                  elapsedText={runningAll && !pausedRun && !restoredRun ? fmtElapsed(elapsed) : null}
                  onRunAll={onRunAll}
                  onPauseAll={onPauseAll}
                  onResumeAll={onResumeAll}
                  t={t}
                />
              }
            />
          </div>

          {/* The pipeline's phases, in its own order. Every one stays mounted (hidden)
              so "Run all" keeps driving them and their in-flight work survives a tab
              switch — which is why this is a hidden div per phase and not a single
              switch on activeTab.

              The number in each heading is the position, not a constant: a phase is
              numbered by its index plus one, and reordering the list renumbers the
              headings by itself. */}
          {phases.map((phase) => (
            <div
              key={phase.id}
              // AND THE HIDDEN ONES ARE NOT PHOTOGRAPHED. A sandbox save rasterises this
              // whole column, and a rasteriser clones what is `display: none` as readily as
              // what is on screen — so one shot was drawing all eight phases to keep one,
              // eight times over. Written by the same expression that hides it, so the two
              // cannot say different things (see sandboxSaves, which reads the attribute).
              {...(activeTab === phase.id ? {} : { 'data-capture-skip': '' })}
              className={activeTab === phase.id ? '' : 'hidden'}
            >
              <EtapeSection
                title={`${t('notice.phase')} ${phaseNumberOf(phase.id)}${t('notice.step.sep')}${t(phase.labelKey)}`}
                info={phase.infoKey ? t(phase.infoKey) : undefined}
              >
                <FolderDoc.Provider value={folder}>
                <PhaseNumber.Provider value={phaseNumberOf(phase.id)}>
                  {phase.render({
                    docId: current.id,
                    file: current.data,
                    name: current.name,
                    numPages,
                    locked: runningAll,
                    held: pausedRun,
                    active: activeTab === phase.id,
                    manual,
                    folder,
                  })}
                </PhaseNumber.Provider>
                </FolderDoc.Provider>
              </EtapeSection>
            </div>
          ))}

          {/* A SAVE BEING TAKEN. Started from the Saves tab, which navigates here with
              ?capture=1 — the pictures are on this page and nowhere else, and a second
              reader mounted off-screen to photograph would be a second PDF, a second set
              of WebGL contexts and no way to see what was captured. */}
          {walking && (
            <SandboxSaveWalk
              docId={current.id}
              docName={current.name}
              tabs={navItems}
              startTab={activeTab}
              onGo={setActiveTab}
              onDone={(err) => {
                setWalking(false);
                navigate(err ? `/saves?err=${encodeURIComponent(err)}` : '/saves');
              }}
            />
          )}

        </div>
      ) : (
        // No document: a drop zone (loading / not-found / empty). A band, not the square it was,
        // which for an icon and three words was a page of white (Louis 2026-10-23). Its middle on
        // the window's middle, as the square's was: 13.5rem is the top bar, this column's pt-4 and
        // half the band's own 14rem.
        <div
          {...dropZoneProps}
          className={`mt-[max(3rem,calc(50dvh-13.5rem))] h-56 overflow-hidden rounded-2xl border shadow-sm transition-colors ${
            dragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-300 bg-white hover:border-blue-500 hover:bg-blue-50'
          } ${SECTION_WIDTH}`}
        >
          {loading ? (
            <div className="flex h-full items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : id ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center text-slate-500 transition-colors hover:bg-blue-50"
            >
              <FileText className="h-10 w-10" />
              <p>{t('notice.viewer.gone')}</p>
            </button>
          ) : (
            <div
              onClick={() => inputRef.current?.click()}
              className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-3 px-6 text-center"
            >
              {busy ? (
                <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
              ) : (
                <Upload className="h-10 w-10 text-slate-400" />
              )}
              {/* The title alone: the whole area takes a drop or a click, and the two lines under
                  it said so at length (Louis 2026-10-23). */}
              <h3 className="text-xl font-semibold text-slate-900">{t('notice.upload')}</h3>
              {rejected && <p className="text-sm text-red-600">{t('notice.viewer.onlyPdf')}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
